import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Message,
  Select,
  Space,
  Steps,
  Typography,
} from "@arco-design/web-react";
import { bitable } from "@lark-base-open/js-sdk";
import { createInstallation, testPat, testPatSetup } from "../api/installations.js";
import { getCatalog, type PlatformManifest } from "../api/platforms.js";
import { useAuthStore } from "../store/authStore.js";
import { parseApiError } from "../api/http.js";

const { Step } = Steps;
const { Title, Paragraph, Text } = Typography;

interface TableOption {
  id: string;
  name: string;
}

export function SetupWizard({ onDone }: { onDone: () => void }): JSX.Element {
  const auth = useAuthStore();
  const [step, setStep] = useState(0);
  const [tables, setTables] = useState<TableOption[]>([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tablesErr, setTablesErr] = useState<string | null>(null);
  const [tableId, setTableId] = useState<string>("");
  const [displayName, setDisplayName] = useState("订单同步");
  const [pat, setPat] = useState("");
  const [patTested, setPatTested] = useState<{ ok: boolean; total?: number; reason?: string } | null>(null);
  const [patTesting, setPatTesting] = useState(false);
  const [robotUrl, setRobotUrl] = useState("");
  const [platforms, setPlatforms] = useState<PlatformManifest[]>([]);
  const [enabledPlatforms, setEnabledPlatforms] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Step 0 加载当前 base 的 table 列表，Step 3 加载平台 catalog
  useEffect(() => {
    bitable.base
      .getTableList()
      .then((tableList: unknown[]) =>
        Promise.all(
          tableList.map(async (t) => {
            const table = t as { id?: string; getName?: () => Promise<string> };
            const id = table.id ?? "";
            const name = table.getName ? await table.getName() : id;
            return { id, name };
          }),
        ),
      )
      .then((list) => {
        setTables(list);
        if (list[0]?.id) setTableId(list[0].id);
      })
      .catch((err: unknown) => setTablesErr((err as Error).message))
      .finally(() => setTablesLoading(false));
  }, []);

  useEffect(() => {
    if (step !== 3) return;
    getCatalog()
      .then((res) => setPlatforms(res.platforms))
      .catch((err: unknown) => Message.error(parseApiError(err).message));
  }, [step]);

  const canNext = (): boolean => {
    if (step === 0) return tableId.length > 0 && displayName.length > 0;
    if (step === 1) return patTested?.ok === true;
    if (step === 2) return true;
    if (step === 3) return enabledPlatforms.length > 0;
    return false;
  };

  const handleTestPat = async (): Promise<void> => {
    if (!pat) {
      Message.warning("请先填入 PAT");
      return;
    }
    if (!auth.appToken) {
      Message.error("飞书上下文丢失（app_token 为空），请刷新插件");
      return;
    }
    setPatTesting(true);
    setPatTested(null);
    try {
      // 走后端 /installations/test-pat-setup 用 setup_token 鉴权
      // 后端再去调 base-api.feishu.cn —— 这样避开浏览器直连飞书 API 的 CORS 限制
      const res = await testPatSetup({ pat, app_token: auth.appToken, table_id: tableId });
      setPatTested(res);
    } catch (err) {
      setPatTested({ ok: false, reason: parseApiError(err).message });
    } finally {
      setPatTesting(false);
    }
  };

  void testPat; // testPat（session 鉴权）保留导出，给已安装后改 PAT 时用

  const handleSubmit = async (): Promise<void> => {
    if (!auth.appToken) {
      Message.error("飞书上下文丢失，请刷新插件");
      return;
    }
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const result = await createInstallation({
        app_token: auth.appToken,
        table_id: tableId,
        display_name: displayName,
        pat,
        ...(robotUrl && { robot_webhook: robotUrl }),
        enabled_platforms: enabledPlatforms,
        ...(auth.tenantKey && { tenant_key: auth.tenantKey }),
      });
      auth.setSession({
        installId: result.installation.install_id,
        sessionToken: result.session_token,
        displayName: result.installation.display_name,
        enabledPlatforms: result.installation.enabled_platforms,
        hasPat: result.installation.has_pat,
        hasRobotWebhook: result.installation.has_robot_webhook,
      });
      Message.success(`安装完成，install_id=${result.installation.install_id}`);
      onDone();
    } catch (err) {
      setSubmitErr(parseApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="os-stack" style={{ maxWidth: 720, margin: "0 auto" }}>
      <Title heading={4}>首次安装：多平台订单同步</Title>
      <Steps current={step} size="small">
        <Step title="选订单表" />
        <Step title="授权码 PAT" />
        <Step title="群机器人" />
        <Step title="启用平台" />
      </Steps>

      <div className="os-card">
        {step === 0 && (
          <Space direction="vertical" style={{ width: "100%" }} size="medium">
            <Paragraph>
              选择当前多维表格里用来存订单数据的「订单主表」。如果你还没建表，先在飞书表格里按
              <Text bold> 23 字段定义</Text> 建好表（见项目文档），然后回到这里。
            </Paragraph>
            {tablesErr && <Alert type="error" content={`读取表格列表失败：${tablesErr}`} />}
            <div>
              <Text bold>订单主表</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                placeholder="选择一张表"
                value={tableId}
                loading={tablesLoading}
                onChange={(v) => setTableId(v as string)}
                options={tables.map((t) => ({ label: t.name, value: t.id }))}
              />
            </div>
            <div>
              <Text bold>显示名</Text>
              <Input
                style={{ marginTop: 6 }}
                value={displayName}
                onChange={setDisplayName}
                maxLength={50}
                showWordLimit
              />
            </div>
          </Space>
        )}

        {step === 1 && (
          <Space direction="vertical" style={{ width: "100%" }} size="medium">
            <Alert
              type="info"
              content={
                <span>
                  请在当前多维表格 <Text bold>右上角「插件」→ 选中本插件 → 点「获取授权码」</Text>{" "}
                  生成一个 PersonalBaseToken，然后复制粘贴到下方。
                </span>
              }
            />
            <Input.Password
              placeholder="开头通常是 pat_..."
              value={pat}
              onChange={setPat}
              autoComplete="off"
            />
            <Space>
              <Button type="primary" loading={patTesting} onClick={handleTestPat}>
                测试 PAT 连通性
              </Button>
              {patTested?.ok && (
                <Text type="success">
                  ✓ PAT 有效{patTested.total !== undefined && `（订单主表当前 ${patTested.total} 条记录）`}
                </Text>
              )}
              {patTested && !patTested.ok && (
                <Text type="error">✗ {patTested.reason ?? "未通过"}</Text>
              )}
            </Space>
          </Space>
        )}

        {step === 2 && (
          <Space direction="vertical" style={{ width: "100%" }} size="medium">
            <Alert
              type="info"
              content={
                <span>
                  可选。在飞书提醒群里 <Text bold>添加自定义机器人</Text>{" "}
                  并复制 webhook 地址（形如 <code>https://open.feishu.cn/open-apis/bot/v2/hook/xxx</code>）。
                  留空则不发群提醒。
                </span>
              }
            />
            <Input
              placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/..."
              value={robotUrl}
              onChange={setRobotUrl}
            />
          </Space>
        )}

        {step === 3 && (
          <Space direction="vertical" style={{ width: "100%" }} size="medium">
            <Paragraph>勾选要启用的平台。每个平台还需要在下一步「店铺」里填入凭证。</Paragraph>
            {platforms.length === 0 && <Text type="secondary">加载平台元数据中…</Text>}
            <Checkbox.Group
              value={enabledPlatforms}
              onChange={(v) => setEnabledPlatforms(v as string[])}
            >
              {platforms.map((p) => (
                <Checkbox key={p.platform} value={p.platform} style={{ display: "block", marginBottom: 8 }}>
                  <Text bold>{p.label}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {p.poll_only ? "（轮询模式）" : "（Webhook + 补偿轮询）"}
                  </Text>
                </Checkbox>
              ))}
            </Checkbox.Group>
            {submitErr && <Alert type="error" content={submitErr} />}
          </Space>
        )}
      </div>

      <div className="os-row" style={{ justifyContent: "space-between" }}>
        <Button disabled={step === 0} onClick={() => setStep(step - 1)}>
          上一步
        </Button>
        {step < 3 ? (
          <Button type="primary" disabled={!canNext()} onClick={() => setStep(step + 1)}>
            下一步
          </Button>
        ) : (
          <Button type="primary" loading={submitting} disabled={!canNext()} onClick={handleSubmit}>
            完成安装
          </Button>
        )}
      </div>
    </div>
  );
}
